import List from 'src/storage/list';

import { dateOfWeek, upperFirstCharAll } from 'src/helpers';

class BaseLocationModel extends List {
  id = 'BaseLocationModel';
  defaultState = () => ({
    current: 'recent',
    hospital: '',
    pageIndex: 0,
    openId: null,
    hospitalId: null,
    place: null,
    cityPlace: null,
    cityAddress: this.user?.cityAddress || this.state.tempAddress?.countryAddress
  });
  listLimit = 10;
  persistent = false;
  refreshOnInit = false;
  refreshOnSubscribe = true;
  apiConfig = {
    list: {
      url: 'hospital/list',
      validation: {
        skip: 'number',
        limit: 'number'
      },
      cancellable: true
    },
    check: {
      url: 'hospital/list',
      validation: {
        skip: 'number',
        limit: 'number'
      },
      cancellable: true
    },
    refresh: {
      url: 'hospital/list',
      validation: {
        limit: 'number'
      },
      cancellable: true
    },
    add: {
      url: 'hospital/add',
      validation: {
        hospital: 'hospital'
      },
      cancellable: true
    },
    del: {
      url: 'hospital/del',
      validation: {
        id: 'string'
      }
    },
    update: {
      url: 'hospital/update',
      validation: {
        id: 'string',
        schedule: 'hospitalSchedule'
      }
    },
    getPlace: 'autocomplete/place',
    findPlace: 'autocomplete/findPlace'
  };

  _events = {
    profile: 'handleProfile',
    location: 'initLocation'
  };

  listParams() {
    let req = {
      skip: this.state.pageIndex * this.listLimit,
      limit: this.listLimit,
      clinicIds: this.state.user?.clinicIds,
      hospitalIds: this.state.user?.hospitalIds
    };
    let query = this.listInput('search');
    if (query.length > 0) {
      req.query = query;
    }
    return req;
  }

  initLocation = (data) => {
    this.cacheState = {
      countryAddress: upperFirstCharAll(data.country) + '_' + data.countryCode,
      countryCode: data.countryCode,
      cityAddress: data.city + '_' + data.countryCode
    };
    this.setState(this.cacheState);
  };

  handleProfile = (nextProfile) => {
    const { password, ...profile } = nextProfile;
    void password;
    let oldHospitalIds,
      newHospitalIds,
      hasChanged = false;
    if (this.state.user) {
      oldHospitalIds = this.state.user?.hospitalIds || [];
      newHospitalIds = profile?.hospitalIds || [];
      hasChanged =
        newHospitalIds.length !== oldHospitalIds.length ||
        newHospitalIds.find((id) => !oldHospitalIds.includes(id));
    }
    this.setState({ ...profile, user: profile });

    if (hasChanged) {
      this.refresh();
    }
  };

  resetLocation = () => {
    this.resetApiState('del');
    this.resetApiState('add');
    this.resetApiState('getPlace');
    this.resetApiState('findPlace');

    this.setState({
      countryAddress: this.cacheState?.countryAddress,
      stateAddress: '',
      cityAddress: '',
      hospital: '',
      otherHospital: '',
      hospitalId: null,
      openId: null,
      place: null,
      cityPlace: null
    });
  };

  saveDate = (cb, exData) => {
    const isClinicDoctor = model.isClinicDoctor();
    let useOther = this.state.hospital === 'other' || !isClinicDoctor;
    const schedule = {};
    dateOfWeek.forEach(
      (date) =>
        (schedule[date.toLowerCase()] = {
          isOpen: false,
          startTime: null,
          endTime: null
        })
    );
    let req = {
      country: this.state.countryAddress,
      state: this.state.stateAddress,
      city: this.state.cityAddress,
      hospital: useOther ? this.state.otherHospital : this.state.hospital,
      hospitalId: useOther ? null : this.state.hospitalId,
      place: this.state.place || '',
      cityPlace: this.state.cityPlace || '',
      schedule,
      ...exData
    };
    this.api('add', req, (error, data, success) => {
      if (success) {
        if (!isClinicDoctor) this.refresh();
        cb(data);
      }
    });
  };

  validRequest(cb) {
    const isClinicDoctor = model.isClinicDoctor();
    let useOther = this.state.hospital === 'other' || !isClinicDoctor;
    let req = {
      country: this.state.countryAddress,
      state: this.state.stateAddress,
      city: this.state.cityAddress,
      hospital: useOther ? this.state.otherHospital : this.state.hospital,
      hospitalId: useOther ? null : this.state.hospitalId
    };

    let error = {},
      hasError = false;
    if (!req.city) {
      error.cityAddress = 'City is required';
      hasError = true;
    }
    if (!req.hospital) {
      error.hospital = 'Hospital is required';
      hasError = true;
    }

    if (!hasError && isClinicDoctor) {
      if (model.state.user.hospitalIds?.includes(this.state.hospitalId)) {
        error.hospital = 'Hospital is already added';
        this.setInvalidApi('add', error);
        cb(false);
      } else {
        cb(true);
      }
      return;
    }
    if (!hasError) {
      this.api(
        'check',
        {
          skip: 0,
          limit: 1,
          hospital: req.hospital,
          city: req.city
        },
        (err, data, success) => {
          if (success && data?.items.length === 0) {
            cb(true);
          } else {
            error.hospital = 'Hospital is already added';
            this.setInvalidApi('add', error);
            cb(false);
          }
        }
      );
    } else {
      this.setInvalidApi('add', error);
      cb(false);
    }
  }

  del = (item, cb) => {
    let req = {
      id: item.id,
      clinicId: item.userId != this.state.user?.id ? item.userId : null
    };
    this.api('del', req, (error, data, success) => {
      if (success) {
        this.refresh();
        cb();
      }
    });
  };

  handleTimesScheduleChange = (item, date, index, key, value) => {
    let schedule = item.schedule || {};
    let times = [...(schedule[date]?.times || [])];
    if (!times[index])
      times[index] = {
        startTime: null,
        endTime: null
      };
    times[index][key] = value;
    this.handleScheduleChange(item, date, 'times', times);
  };

  handleScheduleChange = (item, date, key, value) => {
    if (model.isClinicDoctor()) return;

    let schedule = {};
    if (!item.schedule) {
      dateOfWeek.forEach(
        (date) =>
          (schedule[date.toLowerCase()] = {
            isOpen: false,
            times: [
              {
                startTime: null,
                endTime: null
              }
            ]
          })
      );
    } else {
      schedule = JSON.parse(JSON.stringify(item.schedule));
    }

    schedule[date][key] = value;
    this.api(
      'update',
      { id: item.id, schedule, clinicId: item.clinicId },
      (error, data, success) => {
        if (success) {
          item.schedule = schedule;
          this.setListState({
            items: this.items().map((old) => (old.id === item.id ? item : old))
          });
        } else {
          this.trigger();
        }
      },
      true,
      true
    );

    if (this.invalid) {
      this.trigger();
    }
  };

  isClinicDoctor = () => {
    return this.state.user?.isDoctor && this.state.user?.clinicIds?.length > 0;
  };

  loadPlace = (placeString) => {
    this.cancelApi('getPlace');
    // this.setState({ place: null });
    if (!placeString) return;
    let placeId = placeString.substring(placeString.indexOf('_') + 1);
    if (!placeId) return;
    this.api('getPlace', { placeId }, (error, data, success) => {
      if (success && data?.location) this.setState({ place: data });
    });
  };

  loadCityPlace = (query) => {
    this.cancelApi('findPlace');
    // this.setState({ place: null });
    if (!query) return;
    this.api('findPlace', { query }, (error, data, success) => {
      if (success && data?.location) this.setState({ cityPlace: data });
    });
  };
}

const model = new BaseLocationModel();
export default model;
